import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AiOutlineClose } from "react-icons/ai";

interface TimeoutModalProps {
    isOpen: boolean;
    onClose: () => void; // Logout action
    onDismiss: () => void; // Dismiss modal (keep timer running)
    onExtendSession: () => void;
    timeUntilExpiration: number; // in milliseconds
    isLoading?: boolean;
}

export const TimeoutModal: React.FC<TimeoutModalProps> = ({
    isOpen,
    onClose,
    onDismiss,
    onExtendSession,
    timeUntilExpiration,
    isLoading = false,
}) => {
    const [timeLeft, setTimeLeft] = useState(Math.max(0, Math.floor(timeUntilExpiration / 1000)));
    const [initialTime, setInitialTime] = useState(Math.max(1, Math.floor(timeUntilExpiration / 1000)));
    const onCloseRef = React.useRef(onClose);

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    // Countdown logic
    useEffect(() => {
        if (!isOpen) { return; }

        const initialSeconds = Math.max(0, Math.floor(timeUntilExpiration / 1000));
        console.log("TimeoutModal opened. Time until expiration:", timeUntilExpiration, "ms =", initialSeconds, "seconds");
        setTimeLeft(initialSeconds);
        setInitialTime(initialSeconds || 1); // Avoid division by zero

        if (initialSeconds <= 0) {
            onCloseRef.current();
            return;
        }

        const timer = setInterval(() => {
            setTimeLeft((prev) => {
                const next = prev - 1;
                if (next <= 0) {
                    clearInterval(timer);
                    onCloseRef.current(); // Trigger logout immediately when 0
                    return 0;
                }
                return next;
            });
        }, 1000);

        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]); // Only run when modal opens/closes

    // Formatting time as m:ss
    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? "0" : ""}${s}`;
    };

    // Circular progress calculations
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    const progress = timeLeft / initialTime;
    const strokeDashoffset = circumference - progress * circumference;

    // Colors based on the screenshot
    const colors = {
        primaryGreen: "#10B981", // Emerald-500 for buttons
        primaryOrange: "#F59E0B", // Amber-500 for timer
        trackGray: "#E5E7EB", // Gray-200 for circle track
        textDark: "#111827", // Gray-900
        textLight: "#6B7280", // Gray-500
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop with blur and transparency */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onDismiss}
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                    />

                    {/* Modal Card */}
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 10 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 10 }}
                        transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
                        className="relative w-full max-w-md bg-white shadow-xl rounded-3xl p-8 text-center z-10"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close button */}
                        <button
                            onClick={onDismiss}
                            className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
                            aria-label="Dismiss modal"
                        >
                            <AiOutlineClose size={20} className="text-gray-500" />
                        </button>

                        {/* Circular Timer */}
                        <div className="relative flex items-center justify-center mb-6">
                            <div className="relative w-24 h-24">
                                {/* SVG Ring */}
                                <svg className="w-full h-full transform -rotate-90">
                                    {/* Track Circle */}
                                    <circle
                                        cx="50%"
                                        cy="50%"
                                        r={radius}
                                        fill="none"
                                        stroke={colors.trackGray}
                                        strokeWidth="8"
                                    />
                                    {/* Progress Circle */}
                                    <motion.circle
                                        cx="50%"
                                        cy="50%"
                                        r={radius}
                                        fill="none"
                                        stroke={colors.primaryOrange}
                                        strokeWidth="8"
                                        strokeLinecap="round"
                                        strokeDasharray={circumference}
                                        animate={{ strokeDashoffset }}
                                        transition={{ duration: 1, ease: "linear" }}
                                    />
                                </svg>
                                {/* Time Text */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span
                                        className="text-2xl font-bold"
                                        style={{ color: colors.primaryOrange }}
                                    >
                                        {formatTime(timeLeft)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Content Text */}
                        <h2
                            className="text-2xl font-bold mb-3"
                            style={{ color: colors.textDark }}
                        >
                            You'll be logged out soon
                        </h2>
                        <p className="text-sm mb-6 leading-relaxed" style={{ color: colors.textLight }}>
                            For your security, we'll log you out in{" "}
                            <span className="font-semibold text-gray-900">
                                {Math.ceil(timeLeft / 60)} minutes
                            </span>{" "}
                            unless you choose to stay logged in.
                        </p>
                        <p className="text-sm mb-8" style={{ color: colors.textLight }}>
                            Do you want to stay logged in?
                        </p>

                        {/* Buttons */}
                        <div className="flex gap-4 justify-between">
                            <button
                                onClick={onClose}
                                className="flex-1 py-3 px-4 rounded-xl border transition-colors duration-200 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-offset-1"
                                style={{
                                    borderColor: colors.primaryGreen,
                                    color: colors.primaryGreen,
                                }}
                            >
                                Log out
                            </button>
                            <button
                                onClick={onExtendSession}
                                disabled={isLoading}
                                className="flex-1 py-3 px-4 rounded-xl transition-colors duration-200 font-medium text-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-1 shadow-sm hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                style={{
                                    backgroundColor: colors.primaryGreen,
                                }}
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span>Extending...</span>
                                    </>
                                ) : (
                                    "Stay logged in"
                                )}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};